import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { c } from "../classroomColors";
import { STANDE } from "../standeTokens";
import { spaceApi } from "./spaceApi";
import { Chip, quietButton } from "./parts";
import { clock, dayLabel, plural } from "./spaceTokens";

const KIND = { QUIZ_RACE: "Quiz race", SOLO_SOLVE: "Solo solve", ESCAPE_ROOM: "Escape room" };

const Mark = ({ mark, total }) => {
  if (mark == null) return <Chip>Not sent yet</Chip>;
  const tone = mark >= total ? STANDE.lightGreen : mark > 0 ? STANDE.lightYellow : STANDE.lightRed;
  return (
    <Box as="span" px="9px" h="22px" display="inline-flex" alignItems="center" borderRadius="200px" bg={tone} color={STANDE.black}
      fontSize="12px" fontWeight="600" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {mark}/{total}
    </Box>
  );
};

/** One activity's results, opened from the recap. The teacher sees every
 *  answer with its mark and feedback; a student, their own once sent. */
const Results = ({ sessionGuid, activityGuid, isInstructor, onClose }) => {
  const [view, setView] = useState(null);
  const [problem, setProblem] = useState(null);
  useEffect(() => {
    spaceApi.activity(sessionGuid, activityGuid).then(setView).catch((e) => setProblem(e.message));
  }, [sessionGuid, activityGuid]);

  if (problem) return <Text fontSize="12.5px" color={c.accentSoftText}>{problem}</Text>;
  if (!view) return <Flex align="center" gap="8px" color={c.textSub} fontSize="12.5px"><Spinner size="xs" />Opening…</Flex>;
  const totals = Object.fromEntries(view.questions.map((q) => [q.guid, q.total_mark]));

  return (
    <Box mt="8px" p="12px" borderRadius="12px" bg={c.page} border="1px solid" borderColor={c.borderSoft}>
      <Flex justify="space-between" align="center" mb="8px">
        <Text fontSize="13px" fontWeight="600" color={c.text}>
          {isInstructor && view.average_percent != null ? `Class average ${view.average_percent}%` : "Your answers"}
        </Text>
        <Button size="xs" variant="ghost" color={c.textSub} onClick={onClose}>Close</Button>
      </Flex>
      {view.questions.map((q, i) => {
        const rows = isInstructor
          ? view.responses.filter((r) => r.question_guid === q.guid)
          : view.mine.filter((m) => m.question_guid === q.guid);
        return (
          <Box key={q.guid} py="8px" borderTop={i ? "1px solid" : "none"} borderColor={c.borderSoft}>
            <Text fontSize="12.5px" color={c.textSub} noOfLines={2} mb="6px">
              {i + 1}. {q.question_text}
            </Text>
            {rows.length === 0 && <Text fontSize="12px" color={c.textMuted}>No answers.</Text>}
            {rows.map((r) => (
              <Flex key={r.guid || r.question_guid} gap="10px" align="flex-start" py="4px">
                <Box flex="1" minW={0}>
                  {isInstructor && <Text fontSize="12.5px" fontWeight="600" color={c.text}>{r.student_name}</Text>}
                  <Text fontSize="12.5px" color={c.text} noOfLines={2}>{r.answer_text}</Text>
                  {(isInstructor ? r.final_feedback || r.ai_feedback : r.feedback) && (
                    <Text fontSize="12px" color={c.textSub} noOfLines={3} mt="2px">
                      {isInstructor ? r.final_feedback || r.ai_feedback : r.feedback}
                    </Text>
                  )}
                </Box>
                <Mark mark={isInstructor ? r.final_mark ?? r.ai_mark : r.mark} total={totals[q.guid]} />
              </Flex>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Everything a session ran in its room — quiz races, solo solves, escape
 * rooms — each with how it went, and its results a click away. This is
 * where the work done in class is found again afterwards.
 */
const SessionRecap = ({ session, isInstructor }) => {
  const [items, setItems] = useState(null);
  const [problem, setProblem] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    spaceApi
      .activities(session.guid)
      .then((rows) => setItems(rows || []))
      .catch((e) => {
        setItems([]);
        setProblem(e.message);
      });
  }, [session.guid]);
  useEffect(load, [load]);

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="8px">
        <Text fontSize="13.5px" fontWeight="600" color={c.text}>Ran in the room</Text>
        {items && <Text fontSize="12px" color={c.textMuted}>{items.length}</Text>}
      </Flex>
      {items === null ? (
        <Flex align="center" gap="8px" color={c.textSub} fontSize="12.5px"><Spinner size="xs" />Loading…</Flex>
      ) : problem ? (
        <Text fontSize="12.5px" color={c.accentSoftText}>{problem}</Text>
      ) : items.length === 0 ? (
        <Text fontSize="12.5px" color={c.textSub}>
          {isInstructor ? "Quizzes and escape rooms you run in the room show here, with their results." : "Nothing ran in the room yet."}
        </Text>
      ) : (
        <Flex direction="column" gap="8px">
          {items.map((a) => (
            <Box key={a.guid} p="10px 12px" borderRadius="12px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
              <Flex align="center" gap="10px" wrap="wrap">
                <Box flex="1" minW="160px">
                  <Text fontSize="13px" fontWeight="600" color={c.text}>
                    {KIND[a.kind] || "Activity"} · {plural(a.question_count, "question")}
                  </Text>
                  <Text fontSize="12px" color={c.textSub}>
                    {[dayLabel(a.created_at), clock(a.created_at), a.responders ? plural(a.responders, "student") + " answered" : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </Box>
                {a.kind === "ESCAPE_ROOM" && a.escaped && <Chip tone="blue">Escaped</Chip>}
                {isInstructor && a.average_percent != null && <Chip>{a.average_percent}% average</Chip>}
                {!isInstructor && a.my_marks != null && <Mark mark={a.my_marks} total={a.my_out_of} />}
                <Button {...quietButton} size="xs" h="28px" borderRadius="9px" fontSize="12px"
                  onClick={() => setOpen((o) => (o === a.guid ? null : a.guid))}>
                  {open === a.guid ? "Hide results" : "Results"}
                </Button>
              </Flex>
              {open === a.guid && (
                <Results sessionGuid={session.guid} activityGuid={a.guid} isInstructor={isInstructor} onClose={() => setOpen(null)} />
              )}
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default SessionRecap;
